import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import type CreativeType from '@/Enums/Creatives/CreativeType.enum';
import CreativesTableColumns from '@/Enums/Creatives/CreativesTableColumns.enum';
import { ICreative } from '@/Interfaces/Creatives/Creative.interface';
import { IDV360TicketsCreative } from '@/Interfaces/DV360Tickets/Creative.interface';
import TableActionButton from '@/components/Buttons/TableActionButton';
import ArchivedChip from '@/components/Grid/ArchivedChip';
import VideoUrlCell from '@/components/Grid/VideoUrlCell';
import ExportIcon from '@/components/Icons/ExportIcon';
import TvFilledIcon from '@/components/Icons/TvFilledIcon';
import { CREATIVE_TYPES_ICONS, CREATIVE_TYPES_TITLES } from '@/constants/creativeLibrary';
import { LINE_ITEM_STATUS_ORDER } from '@/constants/itemStatus/chipProps';
import { CreativeStatus } from '@/types/creativeLibrary';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import ArchiveButton from './components/ArchiveButton';
import CheckboxCell from './components/CheckboxCell';
import PreviewCell from './components/PreviewCell';
import StatusCell from './components/StatusCell';

interface IGetColumnsProps {
  handleViewCreative: (row: ICreative | IDV360TicketsCreative) => void;
  handleChangeStatus: (id: number, status: CreativeStatus) => Promise<void>;
  handleArchive: (ids: number[], isArchived: boolean) => Promise<void>;
  canArchive: boolean;
}

type ICreativeRow = IDV360TicketsCreative | ICreative;

export const getColumns = ({
  handleViewCreative,
  handleChangeStatus,
  handleArchive,
  canArchive,
}: IGetColumnsProps): GridColDef[] => [
  ...(canArchive ? [CheckboxCell as GridColDef] : []),
  {
    field: CreativesTableColumns.CREATIVE_NAME,
    headerName: 'Creative Name',
    flex: 1.5,
  },
  {
    field: CreativesTableColumns.CREATIVE_ID,
    headerName: 'Creative ID',
    flex: 0.6,
    align: 'center',
    display: 'flex',
    renderCell: ({ value }: GridRenderCellParams<ICreativeRow, number>) => value || '—',
  },
  {
    field: CreativesTableColumns.THUMBNAIL,
    headerName: 'Preview',
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    sortable: false,
    display: 'flex',
    renderCell: ({ row }: GridRenderCellParams<ICreativeRow>) =>
      row.platform === CampaignPlatformType.Google ? (
        <VideoUrlCell value={(row as IDV360TicketsCreative).videoUrl || ''} />
      ) : (
        <PreviewCell row={row as ICreative} />
      ),
  },
  {
    field: CreativesTableColumns.PLATFORM,
    headerName: 'Platform',
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    sortable: false,
    display: 'flex',
  },
  {
    field: CreativesTableColumns.TYPE,
    headerName: 'Type',
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    display: 'flex',
    renderCell: ({ value }: GridRenderCellParams<ICreativeRow, CreativeType>) => {
      const Icon = CREATIVE_TYPES_ICONS[value!];
      return (
        <div className='flex justify-center items-center'>
          <Icon className={'w-4 mr-1'} />
          <span>{CREATIVE_TYPES_TITLES[value!]}</span>
        </div>
      );
    },
  },
  {
    field: CreativesTableColumns.CLIENT,
    headerName: 'Client',
    align: 'center',
    headerAlign: 'center',
    flex: 1.1,
    display: 'flex',
    renderCell: ({ value }: GridRenderCellParams<ICreativeRow, string>) => (
      <div className='flex justify-start items-center w-full'>
        <div className='bg-[#1B43321A] rounded-2xl w-7 h-7 flex justify-center items-center'>
          <TvFilledIcon className='w-4' />
        </div>
        <span className='ml-3'>{value}</span>
      </div>
    ),
  },
  {
    field: CreativesTableColumns.ADVERTISER,
    headerName: 'Advertiser',
    align: 'center',
    headerAlign: 'center',
    flex: 1.1,
  },
  {
    field: CreativesTableColumns.LINE_ITEMS,
    headerName: 'Line Items',
    align: 'center',
    headerAlign: 'center',
    flex: 0.5,
    display: 'flex',
    renderCell: ({ value, row }: GridRenderCellParams<ICreativeRow, string>) => {
      if (row.platform === CampaignPlatformType.Google) {
        return (
          <span>
            <Chip variant='outlined' label={value} sx={{ width: 56, height: 30 }} />
          </span>
        );
      }

      const lineItems = (row as ICreative).lineItems;

      if (!lineItems?.length) {
        return <Chip variant='outlined' label={value} sx={{ width: 56, height: 30 }} />;
      }
      const sortedLineItems = lineItems
        .slice()
        .sort((a, b) => LINE_ITEM_STATUS_ORDER[a.status] - LINE_ITEM_STATUS_ORDER[b.status]);

      return (
        <Tooltip
          arrow
          placement='top'
          title={
            <Stack spacing={0.5}>
              {sortedLineItems.map(item => (
                <Stack key={item.name} direction='row' spacing={1} justifyContent='space-between'>
                  <Typography variant='caption'>{`${item.name} - ${item.status}`}</Typography>
                </Stack>
              ))}
            </Stack>
          }
          componentsProps={{
            tooltip: {
              sx: {
                maxHeight: 300,
                overflowY: 'auto',
              },
            },
          }}
        >
          <span>
            <Chip variant='outlined' label={value} sx={{ width: 56, height: 30 }} />
          </span>
        </Tooltip>
      );
    },
  },
  {
    field: CreativesTableColumns.STATUS,
    headerName: 'Status',
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    display: 'flex',
    renderCell: ({ row }: GridRenderCellParams<ICreative>) => {
      if (row.platform === CampaignPlatformType.Google) {
        return '-';
      }
      return row.isArchived ? <ArchivedChip /> : <StatusCell row={row} handleChangeStatus={handleChangeStatus} />;
    },
  },
  {
    field: 'action',
    sortable: false,
    headerName: 'Action',
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    display: 'flex',
    renderCell: ({ row }: GridRenderCellParams<ICreative>) => (
      <div className='flex gap-2'>
        <TableActionButton variant='outlined' color='primary' onClick={() => handleViewCreative(row)}>
          <ExportIcon className='w-[11px] mr-[4px]' />
          <span>View</span>
        </TableActionButton>
        {canArchive && row.platform !== CampaignPlatformType.Google && (
          <ArchiveButton row={row} handleArchive={handleArchive} />
        )}
      </div>
    ),
  } as GridColDef,
];
